import { AsyncPipe, NgClass } from '@angular/common';
import { Component, DestroyRef, TemplateRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoPipe } from '@jsverse/transloco';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BirthdateErrorPipe, Country, DayPipe, MonthPipe, NotEmptyPipe, YearPipe } from '@zero/onboarding/domain';
import { PromoCodeService } from '@zero/shared/ui-promo-code/base';
import { ButtonComponent, InputComponent, Item, ModalComponent, SelectComponent } from '@zero/shared/ui-web-design-system';
import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, filter, firstValueFrom, map, switchMap, tap } from 'rxjs';
import { BaseComponent } from '../../components/base.component';
import { OnboardingContainerComponent } from '../../components/onboarding-container/onboarding-container.component';

@Component({
  imports: [
    OnboardingContainerComponent,
    TranslocoPipe,
    AsyncPipe,
    NgClass,
    InputComponent,
    NotEmptyPipe,
    SelectComponent,
    DayPipe,
    MonthPipe,
    YearPipe,
    ModalComponent,
    ButtonComponent,
  ],
  templateUrl: './person.component.html',
  styleUrl: './person.component.scss',
})
export class PersonComponent extends BaseComponent {
  readonly #destroyRef = inject(DestroyRef);
  readonly #promoCodeService = inject(PromoCodeService);
  readonly #modalService = inject(NgbModal);

  readonly #gender = new BehaviorSubject<Gender>(undefined);
  readonly #name = new BehaviorSubject<string>(undefined);
  readonly #lastName = new BehaviorSubject<string>(undefined);
  readonly #promoCode = new BehaviorSubject<string>(undefined);
  readonly #birthDay = new BehaviorSubject<string>(undefined);
  readonly #birthMonth = new BehaviorSubject<string>(undefined);
  readonly #birthYear = new BehaviorSubject<string>(undefined);
  readonly #countries = new BehaviorSubject<Item[]>([]);
  readonly #country = new BehaviorSubject<string>(undefined);
  readonly #city = new BehaviorSubject<string>(undefined);
  readonly #focussedField = new BehaviorSubject<'birthDay' | 'birthMonth' | 'birthYear'>(undefined);
  readonly #pepReason = new BehaviorSubject<string>('');
  readonly #isKwkPromoCode = (promoCode: string) => promoCode?.toLowerCase().startsWith('kwk-');
  #hasInitialPromoCode = false;

  public readonly gender$ = this.#gender.asObservable();
  public readonly name$ = this.#name.asObservable();
  public readonly lastName$ = this.#lastName.asObservable();
  public readonly promoCode$ = this.#promoCode.asObservable();
  public readonly birthDay$ = this.#birthDay.asObservable();
  public readonly birthMonth$ = this.#birthMonth.asObservable();
  public readonly birthYear$ = this.#birthYear.asObservable();
  public readonly country$ = this.#country.asObservable();
  public readonly city$ = this.#city.asObservable();
  public readonly displayMissingGenderError$ = combineLatest([this.gender$, this.name$, this.lastName$]).pipe(
    map(([gender, name, lastName]) => (gender ? false : name || lastName)),
  );
  public readonly birthdateError$ = combineLatest([this.birthDay$, this.birthMonth$, this.birthYear$]).pipe(
    map(([day, month, year]) => day && month && year && new BirthdateErrorPipe().transform(day, month, year)),
  );
  public readonly promoCodeValid$ = this.promoCode$.pipe(
    filter((value) => value && value.trim() !== ''),
    map((value) => (this.#isKwkPromoCode(value) ? value.substring(0, 16) : value)),
    debounceTime(500),
    distinctUntilChanged(),
    switchMap((value) => this.#promoCodeService.checkPromoCode(value)),
    tap((valid) => (this.hasPromoCodeError = !valid)),
  );
  public readonly promoCodeDisabled$ = this.promoCode$.pipe(map((value) => this.#hasInitialPromoCode && this.#isKwkPromoCode(value)));
  public hasPromoCodeError = false;
  public readonly isFormValid$ = combineLatest([
    this.gender$,
    this.name$,
    this.lastName$,
    this.birthDay$,
    this.birthMonth$,
    this.birthYear$,
    this.country$,
    this.city$,
    this.facade.sendingPersonalInformation$,
  ]).pipe(
    map(([gender, name, lastName, birthDay, birthMonth, birthYear, country, city, sendingPersonalInformation]) => {
      return (
        !!gender &&
        new NotEmptyPipe().transform(name) &&
        new NotEmptyPipe().transform(lastName) &&
        new DayPipe().transform(birthDay) &&
        new MonthPipe().transform(birthMonth) &&
        new YearPipe().transform(birthYear) &&
        !!country &&
        new NotEmptyPipe().transform(city) &&
        !sendingPersonalInformation
      );
    }),
  );
  public readonly countries$ = this.#countries.asObservable();
  public readonly pepReason$ = this.#pepReason.asObservable();
  public readonly genders: Gender[] = ['Herr', 'Frau'];
  public displayPromoCodeForm = false;
  public displayPromoCodeFunctionality = true;
  public readonly focussedField$ = this.#focussedField.asObservable();
  public readonly pepReasonMaxLength = 500;

  protected override async submit(): Promise<void> {
    (await firstValueFrom(this.isFormValid$)) &&
      this.facade.sendPersonalInformation({
        gender: this.#gender.value,
        name: this.#name.value?.trim(),
        lastName: this.#lastName.value?.trim(),
        promoCode: this.#promoCode.value?.trim(),
        birthDay: `${this.#birthYear.value}-${this.#birthMonth.value}-${this.#birthDay.value}`,
        country: this.#country.value,
        city: this.#city.value?.trim(),
        pepReason: this.#pepReason.value?.trim(),
      });
  }

  protected override populateFromData(): void {
    combineLatest([this.facade.onboardingData$, this.facade.countries$])
      .pipe(
        filter(([data, countries]) => !!data && !!countries?.length),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(async ([{ anrede, vorname, nachname, promoCode, geburtsdatum, geburtsland, geburtsort }, countries]) => {
        this.#gender.next(anrede as Gender);
        this.#name.next(vorname);
        this.#lastName.next(nachname);
        this.#setPromoCode(promoCode);
        this.#hasInitialPromoCode = !!promoCode;
        this.#setCountry(countries, geburtsland);
        this.#setBirthday(geburtsdatum);
        this.#city.next(geburtsort);
      });
  }

  protected override listenToSuccessfulSubmit(): void {
    this.facade.sentPersonalInformationSuccessfully$
      .pipe(
        filter((sent) => sent),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => this.facade.goToNextStep());
  }

  public toggleGender = (gender: Gender) => this.#gender.next(gender);
  public handleNameChange = (name: string) => this.#name.next(name);
  public handleLastNameChange = (lastName: string) => this.#lastName.next(lastName);
  public handlePromoCodeChange = (promoCode: string) => this.#promoCode.next(promoCode);
  public handleBirthDayChange = (birthDay: string) => {
    this.#birthDay.next(birthDay);
    new DayPipe().transform(birthDay) && this.#focussedField.next('birthMonth');
  };
  public handleBirthMonthChange = (birthMonth: string) => {
    this.#birthMonth.next(birthMonth);
    new MonthPipe().transform(birthMonth) && this.#focussedField.next('birthYear');
  };
  public handleBirthYearChange = (birthYear: string) => this.#birthYear.next(birthYear);
  public selectCountry = (country: Item) => this.#country.next(country.value?.toString());
  public handleCityChange = (city: string) => this.#city.next(city);
  public handlePepReasonChange = (reason: Event) => this.#pepReason.next((reason.target as HTMLTextAreaElement).value?.trim());
  public openPepModal = (content: TemplateRef<HTMLElement>) => this.#modalService.open(content, { centered: true });

  async #setPromoCode(promoCode: string) {
    if (!promoCode) {
      return;
    }
    this.#promoCode.next(promoCode);
    this.displayPromoCodeFunctionality = await firstValueFrom(this.#promoCodeService.shouldShowPromoCodeFunctionality(promoCode));
    this.displayPromoCodeForm = this.displayPromoCodeFunctionality;
  }

  #setCountry(countries: Country[], country: string) {
    country
      ? this.#countries.next([
          ...countries.map((c) => ({
            value: c.isoCode,
            label: c.fullName,
            selected: c.isoCode === country,
          })),
        ])
      : this.#countries.next([
          ...countries.map((c, index: number) => ({
            value: c.isoCode,
            label: c.fullName,
            selected: index === 0,
          })),
        ]);

    this.#country.next(country || (this.#countries.value[0]?.value?.toString() ?? ''));
  }

  #setBirthday(birthday: Date | string) {
    if (!birthday) {
      return;
    }
    const birthDate = new Date(birthday);
    const day = birthDate.getDate();
    this.#birthDay.next(day < 10 ? `0${day.toString()}` : day.toString());
    const month = birthDate.getMonth() + 1;
    this.#birthMonth.next(month < 10 ? `0${month.toString()}` : month.toString());
    this.#birthYear.next(birthDate.getFullYear().toString());
  }
}

type Gender = 'Herr' | 'Frau' | 'Divers';
